import type { IRenderable } from "../../interfaces/IRenderable";
import type { ITransformable } from "../../interfaces/ITransformable";
import type { IStyleable } from "../../interfaces/IStyleable";
import type { IResizable } from "../../interfaces/IResizable";
import type { IDomWrapable } from "../../interfaces/IDomWrapable";

import { RenderManager } from "./RenderManager";
import { EventEmitter } from "./EventEmitter";
import { Matrix } from "./Matrix";
import { Vector } from "./Vector";
import {
    Style,
    type IBackgroundStyle,
    type IBorderStyle,
    type IColorStyle,
    type IFontStyle,
} from "../Styles";

/**
 * Node 构造函数的可选参数接口
 */
export type NodeOptions = {
    /** 节点 ID。如果未提供，将自动生成一个唯一的 ID */
    id?: string;
    /** 基础缩放因子，默认为 1.1 (表示每次缩放10%) */
    zoomFactorBase?: number;
}

/**
 * 节点基类
 */
export abstract class Node
    extends EventEmitter
    implements IRenderable, ITransformable, IStyleable, IResizable {

    /** 节点 ID */
    id: string;

    // 用于生成唯一节点 ID 的静态计数器
    private static _nextNodeId = 0;
    // 父节点引用
    private _parent?: Node;
    // 添加层级属性，默认为0（根节点）
    private _level: number = 0;
    // 样式
    private _style: Style = new Style();
    // 局部变换
    private _localMatrix: Matrix = new Matrix();
    // 全局变换(缓存子节点的全局变换矩阵)
    private _globalMatrix: Matrix = new Matrix();
    // 当前缩放级别
    private _zoomLevel: number = 0;
    // 基础缩放因子 (例如 1.1 表示每次缩放10%)
    private _zoomFactorBase: number;

    /** 渲染目标 */
    abstract target: IDomWrapable;
    /** 节点的宽度 */
    abstract width: number;
    /** 节点的高度 */
    abstract height: number;

    /**
     * Node 构造函数
     * @param options 可选的构造参数对象
     */
    constructor(options?: NodeOptions) {
        super(); // EventEmitter constructor
        const { id, zoomFactorBase = 1.1 } = options || {};

        if (id) {
            this.id = id;
        } else {
            // 使用当前类的名称（小写）作为前缀
            this.id = `${this.constructor.name.toLowerCase()}-${Node._nextNodeId++}`;
        }
        this._zoomFactorBase = zoomFactorBase;
    }

    /** 节点的渲染方法实现 */
    abstract render(): void;

    /**
     * 设置节点的宽度
     * @param width 宽度值
     */
    abstract setWidth(width: number): void;

    /**
     * 设置节点的高度
     * @param height 高度值
     */
    abstract setHeight(height: number): void;

    /**
     * 设置节点的宽高
     * @param size 包含宽度和高度的对象
     */
    abstract setSize(size: { width?: number, height?: number }): void;

    /** 获取父节点 */
    get parent(): Node | undefined {
        return this._parent;
    }
    /** 设置父节点 */
    set parent(value: Node | undefined) {
        this._parent = value;
        this.updateLevel(); // 更新层级
    }

    /** 获取节点层级 */
    get level(): number { return this._level; }

    /** 获取节点样式 */
    get style(): Style { return this._style; }

    /** 获取局部变换矩阵的克隆副本 */
    get localMatrix(): Matrix {
        return this._localMatrix.clone();
    }

    /** 获取全局变换矩阵的克隆副本 */
    get globalMatrix(): Matrix {
        return this._globalMatrix.clone();
    }

    /** 节点的局部坐标 */
    get position(): Vector {
        return new Vector(this._localMatrix.data[0][2], this._localMatrix.data[1][2]);
    }

    /** 节点的局部缩放比例 */
    get scaling(): Vector {
        return new Vector(this._localMatrix.data[0][0], this._localMatrix.data[1][1]);
    }

    /** 节点的局部旋转角度 */
    get rotation(): number {
        return Math.atan2(this._localMatrix.data[1][0], this._localMatrix.data[0][0]);
    }

    /** 节点的全局坐标 */
    get globalPosition(): Vector {
        return new Vector(this._globalMatrix.data[0][2], this._globalMatrix.data[1][2]);
    }

    /** 节点的全局缩放比例 */
    get globalScaling(): Vector {
        return new Vector(this._globalMatrix.data[0][0], this._globalMatrix.data[1][1]);
    }

    /** 节点的全局旋转角度 */
    get globalRotation(): number {
        return Math.atan2(this._globalMatrix.data[1][0], this._globalMatrix.data[0][0]);
    }

    /** 获取当前缩放级别 */
    get zoomLevel(): number {
        return this._zoomLevel;
    }

    /** 获取基础缩放因子 */
    get zoomFactorBase(): number {
        return this._zoomFactorBase;
    }

    /**
     * 更新当前节点的层级
     * @remarks
     * 根据父节点层级计算自身层级，如无父节点则为0级（根节点）
     */
    updateLevel(): void {
        this._level = this._parent ? this._parent.level + 1 : 0;
    }

    // ------------------ 实现 IStyleable 接口 ------------------

    /**
     * 应用字体样式
     * @param fontStyle - 要应用的字体样式对象
     */
    applyFont(fontStyle: IFontStyle): void {
        this.style.fontFamily = fontStyle.fontFamily;
        this.style.fontSize = fontStyle.fontSize;
        this.style.fontStyle = fontStyle.fontStyle;
        this.style.fontWeight = fontStyle.fontWeight;
    }

    /**
     * 应用颜色样式
     * @param colorStyle - 要应用的颜色样式对象
     */
    applyColor(colorStyle: IColorStyle): void {
        this.style.color = colorStyle.color;
    }

    /**
     * 应用边框样式
     * @param borderStyle - 要应用的边框样式对象
     */
    applyBorder(borderStyle: IBorderStyle): void {
        this.style.borderWidth = borderStyle.borderWidth;
        this.style.borderColor = borderStyle.borderColor;
        this.style.borderStyle = borderStyle.borderStyle;
    }

    /**
     * 应用背景样式
     * @param backgroundStyle - 要应用的背景样式对象
     */
    applyBackground(backgroundStyle: IBackgroundStyle): void {
        this.style.backgroundColor = backgroundStyle.backgroundColor;
    }

    // ------------------ 实现 ITransformable 接口 ------------------

    /**
     * 更新全局变换矩阵
     * 
     * @remarks
     * 如果存在父节点，全局变换矩阵是父节点全局变换与自身局部变换的组合
     * 如果是根节点，全局变换矩阵等同于局部变换矩阵
     */
    updateGlobalTransform(): void {
        if (this._parent) {
            // 如果有父节点，全局变换 = 父节点全局变换 × 自身局部变换
            this._globalMatrix = this._parent._globalMatrix.multiply(this._localMatrix);
        } else {
            // 如果是根节点，全局变换等于局部变换
            this._globalMatrix = this._localMatrix.clone();
        }
    }

    /**
     * 将节点标记为需要渲染，并加入渲染队列
     */
    private _queueRender(): void {
        RenderManager.getInstance().queueForRender(this);
    }

    /**
     * 按指定的 x 和 y 距离移动节点
     * @param x - 节点沿 x 轴移动的距离
     * @param y - 节点沿 Y 轴移动的距离
     * @returns 用于方法链的节点实例
     */
    move(x: number, y: number): this {
        const translation = Matrix.translation(x, y);
        this._localMatrix = translation.multiply(this._localMatrix);
        this.updateGlobalTransform();
        this._queueRender();
        return this;
    }

    /**
     * 按指定角度旋转节点
     * @param angle - 以弧度为单位的旋转角度
     * @param cx - 旋转中心的 x 坐标（默认值：0）
     * @param cy - 旋转中心的 Y 坐标（默认值：0）
     * @returns 实例本身，允许方法链
     * 
     * @remarks
     * 如果 cx 和 cy 均为 0，则旋转围绕原点进行。
     * 否则，将围绕指定点 (cx, cy) 旋转。
     * 旋转后，全局变换矩阵会自动更新。
     */
    rotate(angle: number, cx: number = 0, cy: number = 0): this {
        if (cx === 0 && cy === 0) {
            // 围绕原点旋转
            const rotation = Matrix.rotation(angle);
            this._localMatrix = rotation.multiply(this._localMatrix);
        } else {
            // 围绕指定点 (cx, cy) 旋转
            const translateToOrigin = Matrix.translation(-cx, -cy);
            const rotation = Matrix.rotation(angle);
            const translateBack = Matrix.translation(cx, cy);
            // 组合矩阵：translateBack × rotation × translateToOrigin
            const combined = translateBack.multiply(rotation).multiply(translateToOrigin);
            this._localMatrix = combined.multiply(this._localMatrix);
        }
        this.updateGlobalTransform();
        this._queueRender();
        return this;
    }

    /**
     * 沿 X 轴和 Y 轴以指定系数缩放节点
     * @param sx - 沿 X 轴的缩放因子
     * @param sy - 沿 Y 轴的缩放因子
     * @param cx - 缩放中心的 x 坐标（默认值：0）
     * @param cy - 缩放中心的 Y 坐标（默认值：0）
     * @returns 用于方法链的节点实例
     * 
     * @example
     * // 围绕原点水平缩放 2 倍
     * block.scale(2, 1);
     * 
     * @example
     * // 在点（100，100）周围的两个维度上均缩放 0.5 倍
     * block.scale(0.5, 0.5, 100, 100);
     */
    scale(sx: number, sy: number, cx: number = 0, cy: number = 0): this {
        if (cx === 0 && cy === 0) {
            // 围绕原点缩放
            const scaling = Matrix.scaling(sx, sy);
            this._localMatrix = scaling.multiply(this._localMatrix);
        } else {
            // 围绕指定点 (cx, cy) 缩放
            const translateToOrigin = Matrix.translation(-cx, -cy);
            const scaling = Matrix.scaling(sx, sy);
            const translateBack = Matrix.translation(cx, cy);
            // 组合矩阵：translateBack × scaling × translateToOrigin
            const combined = translateBack.multiply(scaling).multiply(translateToOrigin);
            this._localMatrix = combined.multiply(this._localMatrix);
        }
        this.updateGlobalTransform();
        this._queueRender();
        return this;
    }

    /**
     * 移动节点到指定的局部变换坐标
     * @param x - 将节点移动到的目标 x 坐标
     * @param y - 将节点移动到的目标 Y 坐标
     * @returns 用于方法链的节点实例
     */
    moveTo(x: number, y: number): this {
        const { x: currentX, y: currentY } = this.position;
        this.move(x - currentX, y - currentY);
        return this;
    }

    /**
     * 将节点旋转到指定的局部变换角度
     * @param angle - 目标旋转角度（以弧度为单位）
     * @param cx - 旋转中心的 x 坐标（默认值：0）
     * @param cy - 旋转中心的 Y 坐标（默认值：0）
     * @returns 用于方法链的节点实例
     */
    rotateTo(angle: number, cx: number = 0, cy: number = 0): this {
        const currentRotation = this.rotation;
        this.rotate(angle - currentRotation, cx, cy);
        return this;
    }

    /**
     * 将节点缩放到指定的局部变换比例
     * @param sx - 目标 X 轴缩放比例
     * @param sy - 目标 Y 轴缩放比例
     * @param cx - 缩放中心的 x 坐标（默认值：0）
     * @param cy - 缩放中心的 Y 坐标（默认值：0）
     * @returns 用于方法链的节点实例
     */
    scaleTo(sx: number, sy: number, cx: number = 0, cy: number = 0): this {
        const { x: currentScaleX, y: currentScaleY } = this.scaling;
        this.scale(sx / currentScaleX, sy / currentScaleY, cx, cy);
        return this;
    }

    /**
     * 将节点移动到指定的全局变换坐标
     * @param x - 目标全局 x 坐标
     * @param y - 目标全局 Y 坐标
     * @returns 用于方法链的节点实例
     */
    moveToGlobal(x: number, y: number): this {
        const { x: currentX, y: currentY } = this.globalPosition;
        this.move(x - currentX, y - currentY);
        return this;
    }

    /**
     * 将节点旋转到指定的全局变换角度
     * @param angle - 目标全局旋转角度（以弧度为单位）
     * @param cx - 旋转中心的 x 坐标（默认值：0）
     * @param cy - 旋转中心的 Y 坐标（默认值：0）
     * @returns 用于方法链的节点实例
     */
    rotateToGlobal(angle: number, cx: number = 0, cy: number = 0): this {
        const currentRotation = this.globalRotation;
        this.rotate(angle - currentRotation, cx, cy);
        return this;
    }

    /**
     * 将节点缩放到指定的全局变换比例
     * @param sx - 目标全局 X 轴缩放比例
     * @param sy - 目标全局 Y 轴缩放比例
     * @param cx - 缩放中心的 x 坐标（默认值：0）
     * @param cy - 缩放中心的 Y 坐标（默认值：0）
     * @returns 用于方法链的节点实例
     */
    scaleToGlobal(sx: number, sy: number, cx: number = 0, cy: number = 0): this {
        const { x: currentScaleX, y: currentScaleY } = this.globalScaling;
        this.scale(sx / currentScaleX, sy / currentScaleY, cx, cy);
        return this;
    }

    /**
     * 设置基础缩放因子
     * @param factor 新的基础缩放因子
     * @returns 当前节点实例，用于链式调用
     */
    setZoomFactorBase(factor: number): this {
        this._zoomFactorBase = factor;
        this._applyCurrentZoom(); // 可选：立即应用新的因子到当前级别
        return this;
    }

    /**
     * 放大节点
     * @param cx - 缩放中心的 x 坐标（默认值：0）
     * @param cy - 缩放中心的 y 坐标（默认值：0）
     * @returns 当前节点实例，用于链式调用
     */
    zoomIn(cx: number = 0, cy: number = 0): this {
        this._zoomLevel++;
        this._applyCurrentZoom(cx, cy);
        return this;
    }

    /**
     * 缩小节点
     * @param cx - 缩放中心的 x 坐标（默认值：0）
     * @param cy - 缩放中心的 y 坐标（默认值：0）
     * @returns 当前节点实例，用于链式调用
     */
    zoomOut(cx: number = 0, cy: number = 0): this {
        this._zoomLevel--;
        this._applyCurrentZoom(cx, cy);
        return this;
    }

    /**
     * 重置节点的缩放级别到初始状态 (级别0，缩放1)
     * @param cx - 缩放中心的 x 坐标（默认值：0）
     * @param cy - 缩放中心的 y 坐标（默认值：0）
     * @returns 当前节点实例，用于链式调用
     */
    resetZoom(cx: number = 0, cy: number = 0): this {
        this._zoomLevel = 0;
        this._applyCurrentZoom(cx, cy);
        return this;
    }

    /**
     * 根据当前的缩放级别应用缩放
     * @param cx - 缩放中心的 x 坐标（默认值：0）
     * @param cy - 缩放中心的 y 坐标（默认值：0）
     * @remarks
     * 此方法会调用 scaleTo 来设置节点的缩放，确保精确性
     */
    private _applyCurrentZoom(cx: number = 0, cy: number = 0): void {
        const targetScale = Math.pow(this._zoomFactorBase, this._zoomLevel);
        // 调用自身的 scaleTo 方法，该方法应处理局部缩放
        this.scaleTo(targetScale, targetScale, cx, cy);
    }
}